import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Text, Spinner, Badge, Button, Divider,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
} from '@fluentui/react-components';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getSimulationDashboard, type SimulationDashboard, type SimulationRunSummary,
} from '../api/dashboard';
import {
  getSimulation, listSimulations,
  getSimulationSummary, generateSimulationSummary,
  VERDICT_LABEL, verdictColor,
  type ExecutiveSummaryStatusDto, type ExecutiveSummaryDoc,
  type SimulationRunDetailDto, type SimulationResultDto,
} from '../api/simulation';
import type { SignalCareRole } from '../api/types';
import type { ExecSeriesPoint } from '../components/execTypes';
import { MetricCard } from '../components/MetricCard';
import { ExecutiveSummaryPanel } from '../components/ExecutiveSummaryPanel';
import { ScoreBarChart } from '../components/ScoreBarChart';
import { providerBadgeColor, providerLabel } from '../lib/chatModelId';

// ============================================================================
// Constants + helpers
// ============================================================================

const ROLE_LABEL: Record<SignalCareRole, string> = {
  MemberIntake: 'Member Intake',
  CareCoordinator: 'Care Coordinator',
  Lpn: 'LPN',
  Rn: 'RN',
  Doctor: 'Doctor',
  Billing: 'Billing',
  Claims: 'Claims',
};

const EXEC_SUMMARY_LOADING_MESSAGES = [
  'Reading the run\'s results…',
  'Finding which personas broke it…',
  'Weighing the safety verdict…',
  'Writing the executive summary…',
];

function pct(x: number | null): string { return x === null ? '—' : `${(x * 100).toFixed(1)}%`; }
function money(x: number | null): string { return x === null ? '—' : `$${x.toFixed(4)}`; }
function score(x: number | null): string { return x === null ? '—' : x.toFixed(3); }
function pct0(v: number) { return `${(v * 100).toFixed(0)}%`; }
function latencyMs(x: number | null): string { return x === null ? '—' : `${x.toFixed(0)} ms`; }
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}
function scoreColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.75) return 'success';
  if (v >= 0.5) return 'warning';
  return 'danger';
}
function statusColor(status: string): 'success' | 'brand' | 'informative' | 'danger' | 'warning' {
  switch (status) {
    case 'Complete': return 'success';
    case 'Running': return 'brand';
    case 'Queued': return 'informative';
    case 'Failed': return 'danger';
    default: return 'warning';
  }
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles({
  header: { marginBottom: '24px' },
  section: { marginTop: '32px', marginBottom: '16px' },
  grid: {
    display: 'grid',
    gap: '16px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginBottom: '32px',
  },
  err: { color: tokens.colorPaletteRedForeground1 },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },
  sectionHint: {
    color: tokens.colorNeutralForeground3,
    marginBottom: '12px',
    display: 'block',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: '12px 0',
  },
  sectionHead: {
    display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
  },
  matrixLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    fontVariantNumeric: 'tabular-nums',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px',
  },

  // Tables
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2, fontWeight: 600, fontSize: '12px',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    background: tokens.colorNeutralBackground2,
  },
  td: {
    padding: '9px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    color: tokens.colorNeutralForeground1, verticalAlign: 'middle',
  },
  tdRight: {
    padding: '9px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    textAlign: 'right', fontVariantNumeric: 'tabular-nums',
  },
  runRow: { cursor: 'pointer', ':hover': { background: tokens.colorNeutralBackground2 } },
  runRowSelected: { background: tokens.colorBrandBackground2 },
  tally: { display: 'flex', gap: '4px', flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' },

  // Inspector
  inspectGrid: { display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px 16px', marginTop: '8px', fontSize: '13px' },
  inspectLabel: { color: tokens.colorNeutralForeground2, fontWeight: 600 },
  errorBanner: {
    background: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: '8px', padding: '12px 16px', color: tokens.colorPaletteRedForeground1,
  },
  pre: {
    whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
    padding: '10px 12px', borderRadius: '6px',
    background: tokens.colorNeutralBackground3, border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', maxHeight: '220px', overflow: 'auto',
  },
});

// ============================================================================
// Page
// ============================================================================

export default function SimulationResults() {
  const s = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ---- Persona aggregates (moved from Dashboard) -----------------------------
  const [simDash, setSimDash] = useState<SimulationDashboard | null>(null);

  // Executive summary for the latest simulation run (Phase 7 M6).
  const [execSummaryStatus, setExecSummaryStatus] = useState<ExecutiveSummaryStatusDto | null>(null);
  const [execSummaryDoc, setExecSummaryDoc] = useState<ExecutiveSummaryDoc | null>(null);
  const [execSummaryGenerating, setExecSummaryGenerating] = useState(false);
  const [execSummaryErr, setExecSummaryErr] = useState<string | null>(null);
  const [execSummaryMsgIdx, setExecSummaryMsgIdx] = useState(0);
  const execPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- Run history + drill-in ------------------------------------------------
  const [runs, setRuns] = useState<SimulationRunSummary[] | null>(null);
  const [runsErr, setRunsErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SimulationRunDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [inspect, setInspect] = useState<SimulationResultDto | null>(null);
  const deepLinkHandledRef = useRef(false);

  // Rotate exec summary loading messages every 3s while generating.
  useEffect(() => {
    if (!execSummaryGenerating) return;
    const id = setInterval(
      () => setExecSummaryMsgIdx((i) => (i + 1) % EXEC_SUMMARY_LOADING_MESSAGES.length),
      3000,
    );
    return () => clearInterval(id);
  }, [execSummaryGenerating]);

  // Stop exec summary polling interval on unmount.
  useEffect(() => {
    return () => {
      if (execPollRef.current !== null) clearInterval(execPollRef.current);
    };
  }, []);

  // Poll getSimulationSummary every 2.5s until status leaves Generating.
  const startExecSummaryPoll = useCallback((runId: string) => {
    if (execPollRef.current !== null) clearInterval(execPollRef.current);
    execPollRef.current = setInterval(async () => {
      try {
        const dto = await getSimulationSummary(runId);
        setExecSummaryStatus(dto);
        if (dto.status === 'Ready') {
          setExecSummaryDoc(dto.summary);
          setExecSummaryGenerating(false);
          clearInterval(execPollRef.current!);
          execPollRef.current = null;
        } else if (dto.status === 'Failed') {
          setExecSummaryErr(dto.error ?? 'Executive summary generation failed.');
          setExecSummaryGenerating(false);
          clearInterval(execPollRef.current!);
          execPollRef.current = null;
        }
      } catch {
        // non-fatal — keep polling
      }
    }, 2500);
  }, []);

  // Fetch exec summary status when latestRun becomes available.
  useEffect(() => {
    const runId = simDash?.latestRun?.runId;
    if (!runId || simDash?.latestRun?.status !== 'Complete') return;
    let cancelled = false;
    (async () => {
      try {
        const dto = await getSimulationSummary(runId);
        if (cancelled) return;
        setExecSummaryStatus(dto);
        if (dto.status === 'Ready') {
          setExecSummaryDoc(dto.summary);
        } else if (dto.status === 'Generating') {
          setExecSummaryGenerating(true);
          startExecSummaryPoll(runId);
        }
      } catch {
        // non-fatal — summary section just shows generate button
      }
    })();
    return () => { cancelled = true; };
  }, [simDash?.latestRun?.runId, simDash?.latestRun?.status, startExecSummaryPoll]);

  async function handleGenerateExecSummary(force = false) {
    const runId = simDash?.latestRun?.runId;
    if (!runId) return;
    setExecSummaryErr(null);
    setExecSummaryGenerating(true);
    setExecSummaryMsgIdx(0);
    try {
      await generateSimulationSummary(runId, force);
      // Immediately poll — the POST may have returned a ready doc or started generation.
      const dto = await getSimulationSummary(runId);
      setExecSummaryStatus(dto);
      if (dto.status === 'Ready') {
        setExecSummaryDoc(dto.summary);
        setExecSummaryGenerating(false);
      } else if (dto.status === 'Generating') {
        startExecSummaryPoll(runId);
      } else {
        setExecSummaryGenerating(false);
        if (dto.status === 'Failed') setExecSummaryErr(dto.error ?? 'Generation failed.');
      }
    } catch (e) {
      setExecSummaryGenerating(false);
      setExecSummaryErr((e as Error).message);
    }
  }

  function handleExecDrillIn(resultId: string) {
    const runId = simDash?.latestRun?.runId;
    if (!runId) return;
    navigate(`/simulation?run=${runId}&result=${resultId}`);
  }

  // Persona rows → chart points. overallScore = successRate (pass rate for
  // Valid, injection-resistance for Invalid); color-coded by class so the
  // security axis (Invalid) reads distinctly from the quality axis (Valid).
  const simPersonaPoints = useMemo<ExecSeriesPoint[]>(() =>
    (simDash?.personas ?? [])
      .filter((p) => p.successRate !== null)
      .map((p) => ({
        key: `${p.personaClass}:${p.personaLabel}`,
        label: `${p.personaLabel} · ${p.personaClass === 'Valid' ? 'pass' : 'resist'}`,
        color: p.personaClass === 'Valid' ? '#10a37f' : '#7719aa',
        overallScore: p.successRate as number,
        costUsd: p.totalCostUsd,
        latencyMs: p.avgLatencyMs ?? 0,
        hallucinationRisk: 1 - (p.successRate as number),
        isError: false,
      })),
  [simDash]);

  // Load persona aggregates.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const sim = await getSimulationDashboard(30).catch(() => null);
      if (mounted) setSimDash(sim);
    })();
    return () => { mounted = false; };
  }, []);

  // Load one run's detail (used by drill-in + deep-link).
  const loadDetail = useCallback(async (runId: string, autoOpenResultId?: string) => {
    setSelectedRunId(runId);
    setDetailLoading(true);
    setDetailErr(null);
    setInspect(null);
    try {
      const d = await getSimulation(runId);
      setDetail(d);
      if (autoOpenResultId) {
        const target = d.results.find((r) => r.id === autoOpenResultId) ?? null;
        if (target !== null) setInspect(target);
      }
    } catch (e) {
      setDetail(null);
      setDetailErr(`Could not load run results: ${(e as Error).message}`);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Load run history; honor ?run=<id>&result=<id> deep-link once on mount.
  const loadRuns = useCallback(async () => {
    setRefreshing(true);
    setRunsErr(null);
    try {
      const list = await listSimulations(50);
      setRuns(list);
    } catch (e) {
      setRunsErr((e as Error).message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const deepRun = searchParams.get('run');
    if (!deepRun) return;
    deepLinkHandledRef.current = true;
    void loadDetail(deepRun, searchParams.get('result') ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className={s.header}>
        <Title1 as="h1">Simulation Results</Title1>
        <Text>
          Persona-simulation outcomes across every run — 30-day aggregates, the
          executive summary for the latest run, and browsable run history with per-test drill-in.
        </Text>
      </div>

      {/* ------------------- Persona Simulation aggregates (moved from Dashboard) ------------------- */}
      <div className={s.section}>
        <Title3 as="h2">Persona Simulation — 30d rolling</Title3>
        <Text className={s.sectionHint} size={200}>
          Aggregate outcomes from persona simulations. Valid SignalCare roles are
          measured by pass rate + answer quality; invalid red-team personas by
          injection-resistance + safety. Only completed runs in the last 30 days
          are aggregated.{' '}
          <RouterLink to="/simulation" className={s.matrixLink}>Open Simulation →</RouterLink>
        </Text>
      </div>
      <div className={s.card}>
        {simDash === null ? (
          <div className={s.empty}>Loading simulation aggregates…</div>
        ) : !simDash.hasData ? (
          <div className={s.empty}>
            No simulations yet. Launch one from the Simulation page to populate persona breakdowns.
          </div>
        ) : (
          <>
            {simDash.latestRun && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                paddingBottom: 14, marginBottom: 14,
                borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
              }}>
                <Text weight="semibold">{simDash.latestRun.promptName}</Text>
                <Badge appearance="tint">{simDash.latestRun.role}</Badge>
                <Badge appearance="outline" color={simDash.latestRun.mode === 1 ? 'danger' : 'informative'}>
                  {simDash.latestRun.mode === 1 ? 'Live' : 'Mock'}
                </Badge>
                <Badge
                  appearance="tint"
                  color={simDash.latestRun.status === 'Complete' ? 'success'
                    : simDash.latestRun.status === 'Failed' ? 'danger' : 'warning'}
                >
                  {simDash.latestRun.status}
                </Badge>
                <span style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
                  <Badge appearance="tint" color="success">{simDash.latestRun.passed} pass</Badge>
                  <Badge appearance="tint" color="danger">{simDash.latestRun.failed} fail</Badge>
                  <Badge appearance="tint" color="brand">{simDash.latestRun.blocked} blocked</Badge>
                  <Badge appearance="tint" color="brand">{simDash.latestRun.refusedCorrectly} refused</Badge>
                  {simDash.latestRun.errored > 0 && (
                    <Badge appearance="tint" color="warning">{simDash.latestRun.errored} err</Badge>
                  )}
                </div>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {money(simDash.latestRun.totalCostUsd)} · {latencyMs(simDash.latestRun.avgLatencyMs)} · {fmtDate(simDash.latestRun.createdAt)}
                </Text>
              </div>
            )}

            {/* Executive Summary (Phase 7 M6) — placed above persona metric grid */}
            {simDash.latestRun?.status === 'Complete' && (
              <>
                {execSummaryDoc ? (
                  <ExecutiveSummaryPanel
                    summary={execSummaryDoc}
                    onDrillIn={handleExecDrillIn}
                  />
                ) : execSummaryGenerating ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '14px 16px', marginBottom: 16,
                    borderRadius: '8px', border: `1px solid ${tokens.colorNeutralStroke2}`,
                    background: tokens.colorNeutralBackground2,
                  }}>
                    <Spinner size="tiny" />
                    <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                      {EXEC_SUMMARY_LOADING_MESSAGES[execSummaryMsgIdx]}
                    </Text>
                  </div>
                ) : execSummaryErr ? (
                  <div style={{
                    padding: '12px 16px', marginBottom: 16,
                    borderRadius: '8px',
                    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
                    background: tokens.colorPaletteRedBackground2,
                  }}>
                    <Text style={{ color: tokens.colorPaletteRedForeground1 }} block>
                      {execSummaryErr}
                    </Text>
                    <Button
                      appearance="primary"
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={() => void handleGenerateExecSummary(true)}
                    >
                      Retry (force regenerate)
                    </Button>
                  </div>
                ) : execSummaryStatus?.status === 'NotStarted' || execSummaryStatus === null ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', marginBottom: 16,
                    borderRadius: '8px', border: `1px solid ${tokens.colorNeutralStroke2}`,
                    background: tokens.colorNeutralBackground2,
                  }}>
                    <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                      No executive summary yet for this run.
                    </Text>
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={() => void handleGenerateExecSummary(false)}
                    >
                      Generate executive summary
                    </Button>
                  </div>
                ) : null}
              </>
            )}

            <div className={s.grid} style={{ marginBottom: 16 }}>
              <MetricCard label="Valid pass rate" value={pct(simDash.valid.successRate)} hint={`${simDash.valid.testCount} valid-persona tests`} />
              <MetricCard label="Avg answer quality" value={score(simDash.valid.avgQualityScore)} hint="0.0 – 1.0 (valid personas)" />
              <MetricCard label="Injection resistance" value={pct(simDash.invalid.successRate)} hint={`${simDash.invalid.testCount} red-team tests`} />
              <MetricCard label="Avg safety score" value={score(simDash.invalid.avgSafetyScore)} hint="0.0 – 1.0 (invalid personas)" />
            </div>

            {simPersonaPoints.length > 0 ? (
              <ScoreBarChart points={simPersonaPoints} title="Success rate by persona — pass (valid) / resist (invalid)" />
            ) : (
              <div className={s.empty}>No graded persona tests in the window.</div>
            )}
          </>
        )}
      </div>

      {/* ------------------- Run History ------------------- */}
      <div className={s.section}>
        <div className={s.sectionHead}>
          <Title3 as="h2">Run History</Title3>
          <Button
            appearance="secondary"
            size="small"
            onClick={() => void loadRuns()}
            disabled={refreshing}
          >
            {refreshing ? <Spinner size="tiny" label="Refreshing…" /> : 'Refresh'}
          </Button>
        </div>
        <Text className={s.sectionHint} size={200}>
          Every persona-simulation run, newest first. Runs execute in the background —
          use Refresh to pull in ones that have since completed. Click a row to load its
          per-test results below.
        </Text>
      </div>
      <div className={s.card}>
        {runsErr ? (
          <Text className={s.err}>Failed to load run history: {runsErr}</Text>
        ) : runs === null ? (
          <div className={s.empty}>Loading run history…</div>
        ) : runs.length === 0 ? (
          <div className={s.empty}>
            No simulation runs yet. Launch one from the{' '}
            <RouterLink to="/simulation" className={s.matrixLink}>Simulation page</RouterLink>.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th className={s.th}>Date</th>
                  <th className={s.th}>Prompt</th>
                  <th className={s.th}>Role</th>
                  <th className={s.th}>Mode</th>
                  <th className={s.th}>Status</th>
                  <th className={s.th}>Tests</th>
                  <th className={s.th}>Verdicts</th>
                  <th className={s.th}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.runId}
                    className={`${s.runRow} ${r.runId === selectedRunId ? s.runRowSelected : ''}`}
                    onClick={() => void loadDetail(r.runId)}
                  >
                    <td className={s.td}><Text size={200}>{fmtDate(r.createdAt)}</Text></td>
                    <td className={s.td}>{r.promptName}</td>
                    <td className={s.td}>
                      <Badge appearance="tint">{ROLE_LABEL[r.role as SignalCareRole] ?? r.role}</Badge>
                    </td>
                    <td className={s.td}>
                      <Badge appearance="outline" color={r.mode === 1 ? 'danger' : 'informative'}>
                        {r.mode === 1 ? 'Live' : 'Mock'}
                      </Badge>
                    </td>
                    <td className={s.td}>
                      <Badge appearance="tint" color={statusColor(r.status)}>{r.status}</Badge>
                    </td>
                    <td className={s.tdRight}>{r.completedTests}/{r.totalTests}</td>
                    <td className={s.td}>
                      <div className={s.tally}>
                        <Badge appearance="tint" color="success" size="small">{r.passed}P</Badge>
                        <Badge appearance="tint" color="danger" size="small">{r.failed}F</Badge>
                        <Badge appearance="tint" color="brand" size="small">{r.blocked}B</Badge>
                        <Badge appearance="tint" color="brand" size="small">{r.refusedCorrectly}R</Badge>
                        {r.errored > 0 && (
                          <Badge appearance="tint" color="warning" size="small">{r.errored}E</Badge>
                        )}
                      </div>
                    </td>
                    <td className={s.tdRight}>{money(r.totalCostUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ------------------- Selected run detail ------------------- */}
      {selectedRunId && (
        <div className={s.card}>
          {detailLoading ? (
            <Spinner label="Loading run results…" />
          ) : detailErr ? (
            <Text className={s.err}>{detailErr}</Text>
          ) : detail ? (
            <>
              <div className={s.sectionHead} style={{ marginBottom: 8 }}>
                <Title3 as="h2" style={{ margin: 0 }}>Results — {detail.summary.promptName}</Title3>
                <Badge appearance="tint" color="brand">
                  {ROLE_LABEL[detail.summary.role as SignalCareRole] ?? detail.summary.role}
                </Badge>
                <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                  {detail.summary.completedTests} tests · avg {detail.summary.avgLatencyMs.toFixed(0)} ms ·
                  ${detail.summary.totalCostUsd.toFixed(4)}
                </Text>
              </div>
              <Text size={200} block style={{ color: tokens.colorNeutralForeground3, marginBottom: 8 }}>
                Click any row to inspect the full primitive trace and model output.
              </Text>
              <div style={{ overflowX: 'auto' }}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th className={s.th}>Persona</th>
                      <th className={s.th}>Model</th>
                      <th className={s.th}>Verdict</th>
                      <th className={s.th}>Quality</th>
                      <th className={s.th}>Safety</th>
                      <th className={s.th}>Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.results.map((r) => {
                      const vName = VERDICT_LABEL[r.verdict];
                      return (
                        <tr key={r.id} className={s.runRow} onClick={() => setInspect(r)}>
                          <td className={s.td}>
                            <Badge appearance="outline" size="small" color={r.personaClass === 1 ? 'danger' : 'brand'}>
                              {r.personaClass === 1 ? 'Attack' : 'Valid'}
                            </Badge>{' '}
                            <Text size={200}>{r.personaLabel}</Text>
                          </td>
                          <td className={s.td}>
                            <Badge color={providerBadgeColor(r.providerId)} appearance="tint" size="small">
                              {providerLabel(r.providerId)}
                            </Badge>{' '}
                            <Text size={200}>{r.modelName}</Text>
                          </td>
                          <td className={s.td}>
                            <Badge color={verdictColor(vName)} appearance="tint">{vName}</Badge>
                          </td>
                          <td className={s.td}>
                            {r.qualityScore !== null
                              ? <Badge color={scoreColor(r.qualityScore)} appearance="tint" size="small">{pct0(r.qualityScore)}</Badge>
                              : <Text size={100} style={{ color: tokens.colorNeutralForeground4 }}>—</Text>}
                          </td>
                          <td className={s.td}>
                            {r.safetyScore !== null
                              ? <Badge color={scoreColor(r.safetyScore)} appearance="tint" size="small">{pct0(r.safetyScore)}</Badge>
                              : <Text size={100} style={{ color: tokens.colorNeutralForeground4 }}>—</Text>}
                          </td>
                          <td className={s.td}>{r.output.latencyMs.toFixed(0)} ms</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* ------------------- Per-test inspector ------------------- */}
      <Dialog open={inspect !== null} onOpenChange={(_, d) => !d.open && setInspect(null)}>
        <DialogSurface style={{ maxWidth: 860, width: '92vw' }}>
          <DialogBody>
            <DialogTitle>
              {inspect && (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge color={verdictColor(VERDICT_LABEL[inspect.verdict])} appearance="filled">
                    {VERDICT_LABEL[inspect.verdict]}
                  </Badge>
                  <Text weight="semibold">{inspect.personaLabel}</Text>
                  <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
                    · {providerLabel(inspect.providerId)} · {inspect.modelName}
                  </Text>
                </span>
              )}
            </DialogTitle>
            <DialogContent>
              {inspect && (
                <>
                  {inspect.isError && (
                    <div className={s.errorBanner} style={{ marginBottom: 12 }}>
                      <Text size={200}>{inspect.error ?? 'Provider error'}</Text>
                    </div>
                  )}

                  <Title3 as="h3" style={{ fontSize: 15 }}>Primitives — what the model saw</Title3>
                  <div className={s.inspectGrid}>
                    <span className={s.inspectLabel}>Persona profile</span>
                    <Text size={200}>{inspect.primitives.personaProfile || '—'}</Text>
                    <span className={s.inspectLabel}>Model params</span>
                    <Text size={200}>{inspect.primitives.modelParams || '—'}</Text>
                    {inspect.primitives.policyInstructions && (
                      <>
                        <span className={s.inspectLabel}>Policy</span>
                        <Text size={200}>{inspect.primitives.policyInstructions}</Text>
                      </>
                    )}
                  </div>
                  <Text size={200} block style={{ marginTop: 10, marginBottom: 4, color: tokens.colorNeutralForeground2, fontWeight: 600 }}>
                    System prompt
                  </Text>
                  <pre className={s.pre}>{inspect.primitives.systemPrompt || '—'}</pre>
                  <Text size={200} block style={{ marginTop: 10, marginBottom: 4, color: tokens.colorNeutralForeground2, fontWeight: 600 }}>
                    User message
                  </Text>
                  <pre className={s.pre}>{inspect.primitives.userMessage || '—'}</pre>

                  {inspect.primitives.ragContext.length > 0 && (
                    <>
                      <Text size={200} block style={{ marginTop: 10, marginBottom: 4, color: tokens.colorNeutralForeground2, fontWeight: 600 }}>
                        Retrieved context (search)
                      </Text>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {inspect.primitives.ragContext.map((c, i) => (
                          <div key={c.chunkId || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <Badge color={scoreColor(c.score)} appearance="tint" size="small">{pct0(c.score)}</Badge>
                            <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                              {c.text.length > 240 ? `${c.text.slice(0, 240)}…` : c.text}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <Divider style={{ margin: '16px 0' }} />

                  <Title3 as="h3" style={{ fontSize: 15 }}>Output — what the model returned</Title3>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
                    <Badge appearance="tint">{inspect.output.inputTokens} in / {inspect.output.outputTokens} out tok</Badge>
                    <Badge appearance="tint">${inspect.output.estimatedCostUsd.toFixed(6)}</Badge>
                    <Badge appearance="tint">{inspect.output.latencyMs.toFixed(0)} ms</Badge>
                    <Badge appearance="tint">stop: {inspect.output.stopReason || '—'}</Badge>
                    {inspect.qualityScore !== null && (
                      <Badge color={scoreColor(inspect.qualityScore)} appearance="tint">quality {pct0(inspect.qualityScore)}</Badge>
                    )}
                    {inspect.safetyScore !== null && (
                      <Badge color={scoreColor(inspect.safetyScore)} appearance="tint">safety {pct0(inspect.safetyScore)}</Badge>
                    )}
                    {inspect.retrievalScore !== null && (
                      <Badge color={scoreColor(inspect.retrievalScore)} appearance="tint">retrieval {pct0(inspect.retrievalScore)}</Badge>
                    )}
                  </div>
                  <pre className={s.pre}>{inspect.output.answer || '—'}</pre>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setInspect(null)}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
